/* eslint-disable camelcase */

import bridge from '../native';
import { normalizeFacebookProfileData } from './helpers/base-provider';
import { FacebookUserAlreadyExists, EmailTakenError, UnexpectedError, ActivationCodeError } from '../errors';

export default class BaseProvider {
  constructor({ clientId, clientSecret, bearerToken, baseUrl, moesifAppId } = {}, fetch = window.fetch, nativeBridge = bridge) {
    this.clientId = clientId;
    this.clientSecret = clientSecret;
    this.bearerToken = bearerToken;
    this.baseUrl = this._normalizeUrl(baseUrl);
    this.moesifAppId = moesifAppId;

    this.fetch = (url, opts) => fetch(url, opts);
    this.nativeBridge = nativeBridge;
    this.genericNetErrorMsg = 'There was a problem with your request. Please try again later';
  }

  setAuthCredentials(token, refreshToken, expiresIn) {
    this.setBaseProviderAuthCredentials(token, refreshToken, expiresIn);

    this.nativeBridge.setAuthCredentials({
      bearerToken: token,
      refreshToken: refreshToken,
      expiresIn: expiresIn
    });
  }

  setBaseProviderAuthCredentials(token, refreshToken, expiresIn) {
    this.bearerToken = token;
    this.refreshToken = refreshToken;
    this.expiresIn = expiresIn;
  }

  setUserAuthCredentials() {
    this.nativeBridge.setAuthCredentials({
      bearerToken: this.bearerToken,
      refreshToken: this.refreshToken,
      expiresIn: this.expiresIn
    });
  }

  _normalizeUrl(url = '') {
    return url.replace(/\/$/, '');
  }

  _get(url) {
    return this._makeRequest(url, 'GET');
  }

  _post(url, body) {
    return this._makeRequest(url, 'POST', body);
  }

  _put(url, body) {
    return this._makeRequest(url, 'PUT', body);
  }

  _addMoesifHeader(headers) {
    if (this.moesifAppId) {
      headers['X-Moesif-Application-Id'] = this.moesifAppId;
    }
  }

  _signInRequest(body = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'Origin': window.location.href
    };

    this._addMoesifHeader(headers);

    return this.fetch(this.baseUrl + '/oauth/token', {
      method: 'POST',
      body: JSON.stringify(Object.assign(body, {
        'client_id': this.clientId,
        'client_secret': this.clientSecret
      })),
      headers: new Headers(headers),
      mode: 'cors'
    });
  }

  _makeRequest(url, method, body) {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': this._buildAuthHeader(),
      'Origin': window.location.href
    };

    this._addMoesifHeader(headers);

    return this.fetch(this.baseUrl + url, {
      method,
      body: body ? JSON.stringify(body) : undefined,
      headers: new Headers(headers),
      mode: 'cors'
    });
  }

  _buildAuthHeader() {
    return this.bearerToken ?
      `Bearer ${this.bearerToken}` :
      `Basic ${btoa(`${this.clientId}:${this.clientSecret}`)}`;
  }

  async sendResetPasswordEmail(email) {
    const res = await this._post('/v1/users/forgot_password', { email });

    if (!res.ok) throw new Error('Sorry, no user is associated with that email address.');

    return;
  }

  async createAndLoginUser({ email, firstName, lastName, birthday, password, gender, wantsMarketingEmails, wantsMarketingSMS, tos, facebookToken, country, phoneNumber, countryCode }) {
    const res = await this._post('/v1/users', {
      email,
      firstname: firstName,
      lastname: lastName,
      birthday,
      password,
      gender,
      wantsMarketingEmails,
      wantsMarketingSMS,
      tos,
      facebook: facebookToken,
      country,
      phoneNumber,
      countryCode
    });

    const body = await res.json();

    if (!res.ok) {
      switch (body.message) {
        case 'Email already in use.':
          throw new EmailTakenError();
        case 'Facebook user already connected':
          throw new FacebookUserAlreadyExists();
        default:
          throw new Error(body.message || 'Unknown Error');
      }
    }

    await this.loginUser({
      grant_type: 'password',
      username: email,
      password
    });

    return body;
  }

  async loginAndGetUser(payload) {
    await this.loginUser(payload);

    const userProfile = await this.getCurrentUser();
    const userPreferences = await this.getUserPreferences();

    return Object.assign(userProfile, userPreferences);
  }

  async loginUser(payload) {
    const res = await this._signInRequest(payload);

    const body = await res.json();

    if (!res.ok) {
      throw new Error(body.message || 'Unknown Error');
    }

    this.setAuthCredentials(body.access_token, body.refresh_token, body.expires_in);

    return;
  }

  async loginViaPlanetFitnessSSO(code) {
    const res = await this.loginWithPF({ endpoint: 'sso', payload: { code } });

    return res;
  }

  handlePFLoginResponseBody(body) {
    const {
      auth_token: {
        access_token,
        refresh_token,
        expires_in
      },
      first_time,
      merged_account
    } = body;

    this.setBaseProviderAuthCredentials(access_token, refresh_token, expires_in);

    return { first_time, merged_account };
  }

  async loginWithPF({ endpoint, payload }) {
    const res = await this._post('/v1/users/planet-fitness/' + endpoint, payload);

    if (!res.ok) {
      if (res.status === 401) {
        let errorBody;
        try {
          errorBody = await res.json();
        } catch (e) {
          throw new Error(this.genericNetErrorMsg);
        }
        if (errorBody.code === 'UnauthorizedError') {
          throw new Error(errorBody.message);
        }
      }
      throw new Error(this.genericNetErrorMsg);
    }

    try {
      const body = await res.json();

      return this.handlePFLoginResponseBody(body);
    } catch (e) {
      throw new Error(this.genericNetErrorMsg);
    }
  }

  async loginWithPFUsingPassword(payload) {
    const res = await this.loginWithPF({
      endpoint: 'login',
      payload
    });

    return res;
  }

  async loginWithPlanetFitness(payload) {
    const res = await this.loginWithPF({
      endpoint: 'connect',
      payload
    });

    return res;
  }

  async getPlanetFitnessAuthFromNative() {
    try {
      const auth = await this.nativeBridge.getPlanetFitnessAuth();

      return auth;
    } catch (e) {
      throw new Error('Planet Fitness login cancelled');
      // TODO - handle other error responses from native
    }
  }

  async getAppleLoginFromNative() {
    const appleAuth = await this.nativeBridge.logInWithApple();
    if (appleAuth.error) {
      throw new Error('Native');
    }
    const { id_token, authorization_code, given_name, family_name } = appleAuth;
    const res = await this._post('/v1/users/apple/login', {
      idToken: id_token,
      authCode: authorization_code,
      firstName: given_name,
      lastName: family_name
    });
    if (!res.ok) {
      throw new Error(this.genericNetErrorMsg);
    }
    const body = await res.json();
    const {
      auth_token: {
        access_token,
        refresh_token,
        expires_in
      }
    } = body;

    this.setAuthCredentials(access_token, refresh_token, expires_in);
  }

  async acceptToS() {
    await this._put('/v1/me/accept_tos');
  }

  async connectFacebook(facebookToken) {
    const fbToken = encodeURIComponent(facebookToken);
    await this._put(`/v1/me/connect/facebook?token=${fbToken}`);

    return;
  }

  async getUserFacebookProfile() {
    const profileData = await this.nativeBridge.getUserFacebookProfileFromNative();
    return normalizeFacebookProfileData(profileData);
  }

  async updateUser(payload) {
    const res = await this._put('/v1/me', payload);
    const body = await res.json();

    if (!res.ok) {
      throw new Error(body.message || 'Unknown Error');
    }

    return body;
  }

  async getCurrentUser() {
    const res = await this._get('/v1/me');
    if (!res.ok) throw new UnexpectedError();
    const body = await res.json();

    return body;
  }

  async getUserPreferences() {
    const res = await this._get('/v1/me/preferences');
    if (!res.ok) throw new UnexpectedError();
    const body = await res.json();

    return body;
  }

  async updateUserPreferences(payload) {
    const res = await this._put('/v1/me/preferences', payload);
    const body = await res.json();

    if (!res.ok) {
      throw new Error(body.message || 'Unknown Error');
    }

    return body;
  }

  onboardingCompleted(payload) {
    this.nativeBridge.onboardingCompleted(payload);
  }

  async getActivationCode(activationCode) {
    const promoResponse = await this._get(`/v1/promo_codes/${activationCode}`);
    const promoBody = await promoResponse.json();
    if (promoResponse.status === 404) throw new ActivationCodeError();
    if (!promoResponse.ok) throw new UnexpectedError();

    if (promoBody.percent_discount !== 100) throw new ActivationCodeError();
    if (promoBody.start_date && promoBody.start_date > Date.now()) throw new ActivationCodeError();
    if (promoBody.end_date && promoBody.end_date < Date.now()) throw new ActivationCodeError();

    const itemsResponse = await this._get(`/v1/store_items?ids=${promoBody.items.join(',')}`);
    const itemsBody = await itemsResponse.json();
    if (!itemsResponse.ok) throw new UnexpectedError();

    const activationCodeItems = promoBody.items.map(itemId => ({ id: itemId, quantity: 1 }));
    const includesWearable = itemsBody.some(item => item.physical);

    return { activationCode, activationCodeItems, includesWearable };
  }

  async redeemActivationCode(activationCode, items, shippingAddress) {
    const batchPayload = [];
    const chargeRequest = {
      method: 'POST',
      url: '/v1/orders/{{ $.id }}/charge'
    };

    if (shippingAddress) {
      batchPayload.push({
        method: 'PUT',
        url: '/v1/me/shipping',
        body: shippingAddress
      });
    }

    /* eslint-disable camelcase */
    batchPayload.push({
      method: 'POST',
      url: '/v1/orders',
      body: { items, promo_code: activationCode },
      nested: [chargeRequest],
    });
    /* eslint-enable camelcase */

    const response = await this._post('/v1/batch', batchPayload);
    const body = await response.json();

    if (!response.ok) throw new UnexpectedError();

    body.forEach((request) => {
      if ([200, 201].indexOf(request.res.code) < 0) throw new UnexpectedError();
      if (request.nested && request.nested[0] && request.nested[0].res.code !== 200) throw new UnexpectedError();
    });
  }

  async validateAddress(address) {
    const res = await this._post('/v1/address_validation', address);

    if (!res.ok) return { verifyLevel: 'Unverified' };

    const body = await res.json();

    switch (body.verifylevel) {
      case 'InteractionRequired':
      case 'Multiple':
        return { verifyLevel: body.verifylevel, verifyResults: body.results, verifyAddress: body.address };
      case 'Verified':
        return { verifyLevel: 'Verified' };
      default:
        return { verifyLevel: 'Unverified' };
    }
  }

  async saveShippingAddress(address) {
    const res = await this._put('/v1/me/shipping', address);

    if (!res.ok) throw new UnexpectedError();

    return address;
  }

  openSleepApp() {
    const url = window.Android ? 'https://play.google.com/store/apps/details?id=com.ifit.sleep' : 'https://itunes.apple.com/us/app/ifit-sleep/id1107045317';
    this.nativeBridge.openUrl({ isApp: true, url });
  }

  openAltraApp() {
    const url = window.Android ? 'https://play.google.com/store/apps/details?id=com.ifit.altrastandalone' : 'https://itunes.apple.com/us/app/altra-iq/id1107759578';
    this.nativeBridge.openUrl({ isApp: true, url });
  }

  openIfitApps() {
    const url = 'https://www.ifit.com/apps';
    this.nativeBridge.openUrl({ isApp: false, url });
  }

  openTermsOfUse() {
    const url = 'https://www.ifit.com/termsofuse';
    this.nativeBridge.openUrl({ isApp: false, url });
  }

  openPrivacyPolicy() {
    const url = 'https://www.ifit.com/privacypolicy';
    this.nativeBridge.openUrl({ isApp: false, url });
  }

  openForum() {
    const url = 'https://ifit.zendesk.com/hc/en-us';
    this.nativeBridge.openUrl({ isApp: false, url });
  }

  openCoppaHelp() {
    const url = 'http://ifit.io/coppa';
    this.nativeBridge.openUrl({ isApp: false, url });
  }

  async checkEmailExists(email) {
    const res = await this._get(`/v1/users/available/email/${email}`);
    if (res.status !== 204) {
      const body = await res.json();
      return body.message;
    }
  }

  tellNativePageChanged(oldPage, newPage) {
    this.nativeBridge.pageChange({ oldPage, newPage });
  }

  async getUserProfile() {
    const [userProfile, userPreferences] = await Promise.all([
      this.getCurrentUser(),
      this.getUserPreferences()
    ]);

    return Object.assign(userProfile, userPreferences);
  }
}
